// components/Dashboard/widgets/KpiCards.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { useDashboardData } from '../../../hooks/useDashboardData';

// Tentar importar Antd, com fallbacks se não estiver disponível
let Card, Row, Col, Statistic, Spin;
let FileTextOutlined, CalendarOutlined, ClockCircleOutlined, ExclamationCircleOutlined, TrophyOutlined, CheckCircleOutlined;

try {
  const antd = require('antd');
  const icons = require('@ant-design/icons');
  
  Card = antd.Card;
  Row = antd.Row;
  Col = antd.Col;
  Statistic = antd.Statistic;
  Spin = antd.Spin;
  
  FileTextOutlined = icons.FileTextOutlined;
  CalendarOutlined = icons.CalendarOutlined;
  ClockCircleOutlined = icons.ClockCircleOutlined;
  ExclamationCircleOutlined = icons.ExclamationCircleOutlined;
  TrophyOutlined = icons.TrophyOutlined;
  CheckCircleOutlined = icons.CheckCircleOutlined;
} catch (error) {
  // Fallback components se Antd não estiver disponível
  console.warn('Antd não encontrado, usando componentes fallback');
  
  // Card Component com PropTypes
  const CardComponent = ({ children, size, style, bodyStyle, ...props }) => (
    <div 
      {...props}
      style={{ 
        border: '1px solid #d9d9d9', 
        borderRadius: '6px', 
        padding: size === 'small' ? '12px' : '16px',
        backgroundColor: 'white',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        height: '100%',
        ...style 
      }}
    >
      <div style={{ ...bodyStyle }}>
        {children}
      </div>
    </div>
  );
  CardComponent.displayName = 'Card';
  CardComponent.propTypes = {
    children: PropTypes.node,
    size: PropTypes.string,
    style: PropTypes.object,
    bodyStyle: PropTypes.object
  };
  Card = CardComponent;
  
  // Row Component com PropTypes
  const RowComponent = ({ children, gutter, ...props }) => (
    <div 
      {...props}
      style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: gutter ? `${Array.isArray(gutter) ? gutter[0] : gutter}px` : '16px'
      }}
    >
      {children}
    </div>
  );
  RowComponent.displayName = 'Row';
  RowComponent.propTypes = {
    children: PropTypes.node,
    gutter: PropTypes.oneOfType([PropTypes.array, PropTypes.number])
  };
  Row = RowComponent;
  
  // Col Component com PropTypes
  const ColComponent = ({ children, xs, sm, md, ...props }) => (
    <div 
      {...props}
      style={{ 
        flex: `0 0 ${md ? (md/24)*100 : 25}%`, 
        minWidth: xs === 24 ? '100%' : sm === 12 ? '50%' : '200px',
        '@media (max-width: 768px)': {
          flex: '0 0 100%'
        }
      }}
    >
      {children}
    </div>
  );
  ColComponent.displayName = 'Col';
  ColComponent.propTypes = {
    children: PropTypes.node,
    xs: PropTypes.number,
    sm: PropTypes.number,
    md: PropTypes.number
  };
  Col = ColComponent;
  
  // Statistic Component com PropTypes
  const StatisticComponent = ({ title, value, valueStyle, formatter, ...props }) => (
    <div {...props}>
      <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
        {title}
      </div>
      <div style={{ 
        fontSize: '24px', 
        fontWeight: 'bold', 
        color: '#1890ff',
        ...valueStyle 
      }}>
        {formatter ? formatter(value) : (typeof value === 'number' ? value.toLocaleString() : value)}
      </div>
    </div>
  );
  StatisticComponent.displayName = 'Statistic';
  StatisticComponent.propTypes = {
    title: PropTypes.node,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    valueStyle: PropTypes.object,
    formatter: PropTypes.func
  };
  Statistic = StatisticComponent;
  
  // Spin Component com PropTypes
  const SpinComponent = ({ size, ...props }) => (
    <div 
      {...props}
      style={{ 
        display: 'inline-block',
        width: size === 'large' ? '32px' : '16px',
        height: size === 'large' ? '32px' : '16px',
        border: '2px solid #f3f3f3',
        borderTop: '2px solid #1890ff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}
    />
  );
  SpinComponent.displayName = 'Spin';
  SpinComponent.propTypes = {
    size: PropTypes.string
  };
  Spin = SpinComponent;
  
  // Ícones como componentes com displayName
  const FileTextIcon = () => <span>📄</span>;
  FileTextIcon.displayName = 'FileTextOutlined';
  FileTextOutlined = FileTextIcon;
  
  const CalendarIcon = () => <span>📅</span>;
  CalendarIcon.displayName = 'CalendarOutlined';
  CalendarOutlined = CalendarIcon;
  
  const ClockIcon = () => <span>🕐</span>;
  ClockIcon.displayName = 'ClockCircleOutlined';
  ClockCircleOutlined = ClockIcon;
  
  const ExclamationIcon = () => <span>⚠️</span>;
  ExclamationIcon.displayName = 'ExclamationCircleOutlined';
  ExclamationCircleOutlined = ExclamationIcon;
  
  const TrophyIcon = () => <span>🏆</span>;
  TrophyIcon.displayName = 'TrophyOutlined';
  TrophyOutlined = TrophyIcon;
  
  const CheckIcon = () => <span>✅</span>;
  CheckIcon.displayName = 'CheckCircleOutlined';
  CheckCircleOutlined = CheckIcon;
}

// Adicionar animação de spin se não existir
if (typeof document !== 'undefined' && !document.querySelector('#spin-animation')) {
  const style = document.createElement('style');
  style.id = 'spin-animation';
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

const KpiCards = ({ endpoint, filtros }) => {
  const { dados, loading, error, hasData } = useDashboardData(endpoint, filtros);

  // Loading state
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px', color: '#666', fontSize: '14px' }}>
          Carregando KPIs...
        </p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        backgroundColor: '#fff2f0',
        border: '1px solid #ffccc7',
        borderRadius: '6px',
        color: '#a8071a'
      }}>
        <ExclamationCircleOutlined style={{ 
          fontSize: '24px', 
          marginBottom: '8px',
          display: 'block'
        }} />
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
          Erro ao carregar KPIs
        </p>
        <small style={{ color: '#cf1322' }}>{error}</small>
      </div>
    );
  }

  // Empty state
  if (!hasData) {
    return (
      <div style={{ 
        padding: '40px 20px', 
        textAlign: 'center', 
        backgroundColor: '#fafafa',
        border: '1px dashed #d9d9d9',
        borderRadius: '6px',
        color: '#999'
      }}>
        <FileTextOutlined style={{ 
          fontSize: '32px', 
          marginBottom: '12px',
          display: 'block'
        }} />
        <p style={{ margin: 0 }}>Nenhum dado disponível</p>
      </div>
    );
  }

  // Extrair dados dos KPIs
  const kpis = dados?.cards || [];

  // Função para mapear ícones
  const getIconComponent = (iconName) => {
    const icons = {
      'FileText': <FileTextOutlined />,
      'Calendar': <CalendarOutlined />,
      'Clock': <ClockCircleOutlined />,
      'AlertTriangle': <ExclamationCircleOutlined />,
      'Trophy': <TrophyOutlined />,
      'CheckCircle': <CheckCircleOutlined />
    };
    return icons[iconName] || <FileTextOutlined />;
  };

  // Função para mapear cores
  const getCardColor = (cor) => {
    const cores = {
      'blue': '#1890ff',
      'green': '#52c41a', 
      'orange': '#fa8c16',
      'red': '#f5222d',
      'purple': '#722ed1',
      'cyan': '#13c2c2',
      'gold': '#faad14',
      'lime': '#a0d911'
    };
    return cores[cor] || '#1890ff';
  };

  // Formatador de valores
  const formatValue = (value) => {
    if (typeof value === 'number') {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`;
      }
      return value.toLocaleString();
    }
    return value;
  };

  // Renderizar os cards
  return (
    <Row gutter={[16, 16]}>
      {kpis.map((kpi, index) => (
        <Col xs={24} sm={12} md={6} key={index}>
          <Card 
            size="small"
            style={{ 
              borderLeft: `4px solid ${getCardColor(kpi.cor)}`,
              height: '100%',
              transition: 'all 0.2s',
              cursor: 'default'
            }}
            bodyStyle={{ padding: '16px' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Statistic
              title={
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <span style={{ 
                    color: getCardColor(kpi.cor), 
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {getIconComponent(kpi.icone)}
                  </span>
                  <span style={{ 
                    fontSize: '12px', 
                    fontWeight: '500',
                    color: '#666',
                    lineHeight: '1.2'
                  }}>
                    {kpi.titulo}
                  </span>
                </div>
              }
              value={kpi.valor}
              valueStyle={{ 
                color: getCardColor(kpi.cor),
                fontSize: '28px',
                fontWeight: 'bold',
                lineHeight: '1',
                marginTop: '4px'
              }}
              formatter={formatValue}
            />
          </Card>
        </Col>
      ))}
    </Row>
  );
};

// PropTypes
KpiCards.displayName = 'KpiCards';
KpiCards.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object
};

KpiCards.defaultProps = {
  filtros: {}
};

export default KpiCards;